import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';

const SubscriptionDetails = ({ subscription: initialSubscription, onBack, onRefresh }) => {
  const [subscription, setSubscription] = useState(initialSubscription);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    package: subscription?.package || '',
    status: subscription?.status || '',
    startDate: subscription?.startDate ? new Date(subscription.startDate).toISOString().split('T')[0] : '',
    endDate: subscription?.endDate ? new Date(subscription.endDate).toISOString().split('T')[0] : '',
    autoRenew: subscription?.autoRenew || false,
    notes: subscription?.notes || '',
  });

  useEffect(() => {
    if (initialSubscription) {
      setSubscription(initialSubscription);
      setFormData({
        package: initialSubscription.package || '',
        status: initialSubscription.status || '',
        startDate: initialSubscription.startDate ? new Date(initialSubscription.startDate).toISOString().split('T')[0] : '',
        endDate: initialSubscription.endDate ? new Date(initialSubscription.endDate).toISOString().split('T')[0] : '',
        autoRenew: initialSubscription.autoRenew || false,
        notes: initialSubscription.notes || '',
      });
    }
  }, [initialSubscription]);

  const fetchSubscription = async () => {
    if (!subscription?._id) return;
    setLoading(true);
    try {
      const data = await apiRequest(`/admin/subscriptions/${subscription._id}`);
      if (data && data.success) {
        setSubscription(data.data);
        setFormData({
          package: data.data.package || '',
          status: data.data.status || '',
          startDate: data.data.startDate ? new Date(data.data.startDate).toISOString().split('T')[0] : '',
          endDate: data.data.endDate ? new Date(data.data.endDate).toISOString().split('T')[0] : '',
          autoRenew: data.data.autoRenew || false,
          notes: data.data.notes || '',
        });
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const data = await apiRequest(
        `/admin/subscriptions/${subscription._id}`,
        {
          method: 'PUT',
          body: JSON.stringify(formData),
        }
      );
      if (data.success) {
        setMessage({ type: 'success', text: 'تم تحديث الاشتراك بنجاح' });
        setEditMode(false);
        fetchSubscription();
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = async () => {
    if (!window.confirm('هل أنت متأكد من تجديد الاشتراك لمدة سنة إضافية؟')) {
      return;
    }

    setLoading(true);
    try {
      const data = await apiRequest(
        `/admin/subscriptions/${subscription._id}/renew`,
        { method: 'POST' }
      );
      if (data.success) {
        setMessage({ type: 'success', text: 'تم تجديد الاشتراك بنجاح' });
        fetchSubscription();
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('هل أنت متأكد من إلغاء الاشتراك؟')) {
      return;
    }

    setLoading(true);
    try {
      const data = await apiRequest(
        `/admin/subscriptions/${subscription._id}/cancel`,
        { method: 'POST' }
      );
      if (data.success) {
        setMessage({ type: 'success', text: 'تم إلغاء الاشتراك بنجاح' });
        fetchSubscription();
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!window.confirm('هل أنت متأكد من إعادة تفعيل الاشتراك؟')) {
      return;
    }

    setLoading(true);
    try {
      const data = await apiRequest(
        `/admin/subscriptions/${subscription._id}/reactivate`,
        { method: 'POST' }
      );
      if (data.success) {
        setMessage({ type: 'success', text: 'تم إعادة تفعيل الاشتراك بنجاح' });
        fetchSubscription();
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
    } finally {
      setLoading(false);
    }
  };

  if (!subscription) {
    return <div className="empty-state">لا يوجد اشتراك محدد</div>;
  }

  const getStatusBadge = (status, endDate) => {
    const isExpired = new Date(endDate) < new Date();
    if (status === 'cancelled') {
      return <span className="badge badge-danger">ملغي</span>;
    }
    if (status === 'expired' || isExpired) {
      return <span className="badge badge-danger">منتهي</span>;
    }
    if (status === 'active') {
      return <span className="badge badge-success">نشط</span>;
    }
    return <span className="badge badge-info">قيد الانتظار</span>;
  };

  const getPackageLabel = (pkg) => {
    const labels = {
      basic: 'أساسي',
      standard: 'قياسي',
      premium: 'مميز'
    };
    return labels[pkg] || pkg;
  };

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">تفاصيل الاشتراك</h2>
          <button className="btn btn-secondary btn-sm" onClick={onBack}>
            رجوع
          </button>
        </div>

        {message.text && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
            <span>{message.type === 'success' ? '✅' : '❌'}</span>
            <span>{message.text}</span>
          </div>
        )}

        {!editMode ? (
          <div>
            <div style={{ marginBottom: '1rem' }}>
              <p><strong>العميل:</strong> {subscription.customerId?.name || '—'}</p>
              <p><strong>البريد الإلكتروني:</strong> {subscription.customerId?.email || '—'}</p>
              <p><strong>الباقة:</strong> <span className="badge badge-info">{getPackageLabel(subscription.package)}</span></p>
              <p><strong>الحالة:</strong> {getStatusBadge(subscription.status, subscription.endDate)}</p>
              <p><strong>تاريخ البدء:</strong> {new Date(subscription.startDate).toLocaleDateString('ar-SA')}</p>
              <p><strong>تاريخ الانتهاء:</strong> {new Date(subscription.endDate).toLocaleDateString('ar-SA')}</p>
              <p><strong>التجديد التلقائي:</strong> {subscription.autoRenew ? <span className="badge badge-success">نعم</span> : <span className="badge badge-secondary">لا</span>}</p>
              {subscription.notes && <p><strong>ملاحظات:</strong> {subscription.notes}</p>}
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={() => setEditMode(true)}>
                تعديل
              </button>
              {subscription.status === 'active' && (
                <button className="btn btn-success btn-sm" onClick={handleRenew} disabled={loading}>
                  تجديد
                </button>
              )}
              {subscription.status === 'cancelled' && (
                <button className="btn btn-success btn-sm" onClick={handleReactivate} disabled={loading}>
                  إعادة تفعيل
                </button>
              )}
              {subscription.status !== 'cancelled' && (
                <button className="btn btn-danger btn-sm" onClick={handleCancel} disabled={loading}>
                  إلغاء
                </button>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate}>
            <div className="form-group">
              <label className="form-label">الباقة</label>
              <select
                className="form-control"
                value={formData.package}
                onChange={(e) => setFormData({ ...formData, package: e.target.value })}
              >
                <option value="basic">أساسي</option>
                <option value="standard">قياسي</option>
                <option value="premium">مميز</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">الحالة</label>
              <select
                className="form-control"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="pending">قيد الانتظار</option>
                <option value="active">نشط</option>
                <option value="expired">منتهي</option>
                <option value="cancelled">ملغي</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">تاريخ البدء</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">تاريخ الانتهاء</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.autoRenew}
                  onChange={(e) => setFormData({ ...formData, autoRenew: e.target.checked })}
                />
                <span>التجديد التلقائي</span>
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">ملاحظات</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setEditMode(false)}>
                إلغاء
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'جاري الحفظ...' : 'حفظ'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SubscriptionDetails;

